import React, { useState, useMemo, useRef, useCallback } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { scaleLinear } from 'd3-scale';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// ISO Alpha-2 to ISO 3166-1 numeric mapping (used by world-atlas TopoJSON)
const ISO_A2_TO_NUMERIC = {
  AF:"004",AL:"008",DZ:"012",AD:"020",AO:"024",AG:"028",AR:"032",AM:"051",AU:"036",
  AT:"040",AZ:"031",BS:"044",BH:"048",BD:"050",BB:"052",BY:"112",BE:"056",BZ:"084",
  BJ:"204",BT:"064",BO:"068",BA:"070",BW:"072",BR:"076",BN:"096",BG:"100",BF:"854",
  BI:"108",CV:"132",KH:"116",CM:"120",CA:"124",CF:"140",TD:"148",CL:"152",CN:"156",
  CO:"170",KM:"174",CG:"178",CD:"180",CR:"188",CI:"384",HR:"191",CU:"192",CY:"196",
  CZ:"203",DK:"208",DJ:"262",DM:"212",DO:"214",EC:"218",EG:"818",SV:"222",GQ:"226",
  ER:"232",EE:"233",SZ:"748",ET:"231",FJ:"242",FI:"246",FR:"250",GA:"266",GM:"270",
  GE:"268",DE:"276",GH:"288",GR:"300",GD:"308",GT:"320",GN:"324",GW:"624",GY:"328",
  HT:"332",HN:"340",HU:"348",IS:"352",IN:"356",ID:"360",IR:"364",IQ:"368",IE:"372",
  IL:"376",IT:"380",JM:"388",JP:"392",JO:"400",KZ:"398",KE:"404",KI:"296",KP:"408",
  KR:"410",KW:"414",KG:"417",LA:"418",LV:"428",LB:"422",LS:"426",LR:"430",LY:"434",
  LI:"438",LT:"440",LU:"442",MG:"450",MW:"454",MY:"458",MV:"462",ML:"466",MT:"470",
  MH:"584",MR:"478",MU:"480",MX:"484",FM:"583",MD:"498",MN:"496",ME:"499",MA:"504",
  MZ:"508",MM:"104",NA:"516",NR:"520",NP:"524",NL:"528",NZ:"554",NI:"558",NE:"562",
  NG:"566",NO:"578",OM:"512",PK:"586",PW:"585",PA:"591",PG:"598",PY:"600",PE:"604",
  PH:"608",PL:"616",PT:"620",QA:"634",RO:"642",RU:"643",RW:"646",KN:"659",LC:"662",
  VC:"670",WS:"882",ST:"678",SA:"682",SN:"686",RS:"688",SC:"690",SL:"694",SG:"702",
  SK:"703",SI:"705",SB:"090",SO:"706",ZA:"710",SS:"728",ES:"724",LK:"144",SD:"736",
  SR:"740",SE:"752",CH:"756",SY:"760",TW:"158",TJ:"762",TZ:"834",TH:"764",TL:"626",
  TG:"768",TO:"776",TT:"780",TN:"788",TR:"792",TM:"795",TV:"798",UG:"800",UA:"804",
  AE:"784",GB:"826",US:"840",UY:"858",UZ:"860",VU:"548",VE:"862",VN:"704",YE:"887",
  ZM:"894",ZW:"716",PS:"275",HK:"344",MO:"446",XK:"010",PR:"630"
};

// Reverse mapping: numeric -> Alpha-2 (for tooltip display)
const NUMERIC_TO_ISO_A2 = Object.fromEntries(
  Object.entries(ISO_A2_TO_NUMERIC).map(([a2, num]) => [num, a2])
);

const ActiveUsersHeatmap = ({ data = [], isDarkMode, periodLabel }) => {
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ coordinates: [0, 20], zoom: 1 });
  const containerRef = useRef(null);

  // Build a map of numeric country ID -> active users
  const countryDataMap = useMemo(() => {
    const map = {};
    data.forEach(item => {
      if (item.country) {
        const numericId = ISO_A2_TO_NUMERIC[item.country.toUpperCase()];
        if (numericId) {
          map[numericId] = item.activeUsers;
        }
      }
    });
    return map;
  }, [data]);

  // Color scale from light to dark purple
  const maxUsers = useMemo(() => {
    if (data.length === 0) return 1;
    return Math.max(...data.map(d => d.activeUsers), 1);
  }, [data]);

  const colorScale = useMemo(() => {
    // Theme colors: light → bright → dark (higher users = darker)
    return scaleLinear()
      .domain([0, maxUsers * 0.02, maxUsers * 0.08, maxUsers * 0.2, maxUsers * 0.4, maxUsers * 0.7, maxUsers])
      .range(isDarkMode
        ? ['#1f2937', '#2d2560', '#3491f7', '#af3eff', '#8C3CF9', '#5a1db3', '#2e0d6b']
        : ['#f5f0ff', '#d8bfff', '#af3eff', '#8C3CF9', '#7c2de2', '#5a1db3', '#2e0d6b']
      );
  }, [maxUsers, isDarkMode]);

  const handleMouseEnter = (e, geo) => {
    const numericId = geo.id;
    const countryName = numericId === '376' ? 'Palestine' : geo.properties.name;
    const users = countryDataMap[numericId] || 0;
    const isoCode = numericId === '376' ? 'PS' : (NUMERIC_TO_ISO_A2[numericId] || '');
    setTooltipContent(`${countryName}${isoCode ? ` (${isoCode})` : ''}: ${users.toLocaleString()} active users`);
  };

  const handleMouseLeave = () => {
    setTooltipContent('');
  };

  // Track mouse via native event on the container for accurate coordinates
  const handleContainerMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltipPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  }, []);

  const handleZoomIn = useCallback(() => {
    setPosition(pos => ({ ...pos, zoom: Math.min(pos.zoom * 1.5, 8) }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setPosition(pos => ({ ...pos, zoom: Math.max(pos.zoom / 1.5, 1) }));
  }, []);

  const handleReset = useCallback(() => {
    setPosition({ coordinates: [0, 20], zoom: 1 });
  }, []);

  const handleMoveEnd = useCallback((pos) => {
    setPosition(pos);
  }, []);

  // Legend steps
  const legendSteps = 7;
  const legendValues = Array.from({ length: legendSteps + 1 }, (_, i) =>
    Math.round((maxUsers / legendSteps) * i)
  );

  return (
    <div className="chart-container">
      <div className="chart-header" style={{ marginBottom: '0' }}>
        <h3>Active Users by Country</h3>
        <span className="chart-badge">{periodLabel}</span>
      </div>

      <div
        ref={containerRef}
        style={{ position: 'relative', flex: 1, overflow: 'hidden' }}
        onMouseMove={handleContainerMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <ComposableMap
          projectionConfig={{ scale: 147 }}
          style={{ width: '100%', marginTop: '-14%', marginBottom: '-5%' }}
        >
          <ZoomableGroup
            center={position.coordinates}
            zoom={position.zoom}
            onMoveEnd={handleMoveEnd}
            minZoom={1}
            maxZoom={8}
            filterZoomEvent={(evt) => evt.type !== 'wheel'}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map(geo => {
                  const numericId = geo.id;
                  const users = countryDataMap[numericId] || 0;

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={users > 0 ? colorScale(users) : (isDarkMode ? '#1f2937' : '#f5f0ff')}
                      stroke={isDarkMode ? '#4b5563' : '#d1d5db'}
                      strokeWidth={0.5}
                      style={{
                        default: { outline: 'none', cursor: 'grab' },
                        hover: {
                          outline: 'none',
                          fill: '#3491f7',
                          stroke: isDarkMode ? '#e5e7eb' : '#333',
                          strokeWidth: 1
                        },
                        pressed: { outline: 'none', cursor: 'grabbing' }
                      }}
                      onMouseEnter={(e) => handleMouseEnter(e, geo)}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {/* Zoom Controls */}
        <div
          style={{
            position: 'absolute',
            bottom: '8%',
            left: '0.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            zIndex: 10
          }}
        >
          {[
            { label: '+', onClick: handleZoomIn, title: 'Zoom in' },
            { label: '\u2212', onClick: handleZoomOut, title: 'Zoom out' },
            { label: '\u21BA', onClick: handleReset, title: 'Reset zoom' }
          ].map(({ label, onClick, title }) => (
            <button
              key={title}
              onClick={onClick}
              title={title}
              style={{
                width: '28px',
                height: '28px',
                border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '6px',
                background: isDarkMode ? '#1f2937' : '#ffffff',
                color: isDarkMode ? '#e5e7eb' : '#333333',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                padding: 0,
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = isDarkMode ? '#374151' : '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.background = isDarkMode ? '#1f2937' : '#ffffff'}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Tooltip */}
        {tooltipContent && (
          <div
            style={{
              position: 'absolute',
              left: tooltipPos.x + 12,
              top: tooltipPos.y - 28,
              background: isDarkMode ? '#1f2937' : '#ffffff',
              color: isDarkMode ? '#e5e7eb' : '#333333',
              border: `1px solid ${isDarkMode ? '#4b5563' : '#e5e7eb'}`,
              borderRadius: '8px',
              padding: '0.5rem 0.75rem',
              fontSize: '0.8rem',
              fontWeight: 600,
              pointerEvents: 'none',
              zIndex: 1000,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap'
            }}
          >
            {tooltipContent}
          </div>
        )}

        {/* Legend */}
        <div
          style={{
            position: 'absolute',
            top: '2%',
            right: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.7rem',
            color: isDarkMode ? '#9ca3af' : '#6b7280'
          }}
        >
          <span>0</span>
          <div style={{ display: 'flex', height: '10px', borderRadius: '4px', overflow: 'hidden' }}>
            {legendValues.slice(0, -1).map((val, i) => (
              <div
                key={i}
                style={{
                  width: '24px',
                  height: '10px',
                  background: colorScale(val)
                }}
              />
            ))}
          </div>
          <span>{maxUsers.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

export default ActiveUsersHeatmap;
